"use client";

import React, { useEffect } from "react";
import Image from "next/image";
import { motion } from "framer-motion";

import { Package } from "@/domain/entities/package.entity";
import { Theme } from "@/domain/entities/theme.entity";
import { AsyncStatus, ASYNC_STATUS } from "@/hooks/use-booking-flow";
import { cn } from "@/lib/utils";
import { getImageUrl } from "@/shared/utils/image";

interface ThemeStepProps {
  mode: "single" | "multi" | "all-inclusive";
  selectedPackage: Package | null;
  themes: Theme[];
  isLoading: boolean;
  status: AsyncStatus;
  selectedThemes: Theme[];
  errorThemes: Record<string, boolean>;
  slotCount: number;
  onFetchThemes: () => void | Promise<void>;
  onThemeImageError: (themeId: string) => void;
  onSelectThemeForSlot: (slotIndex: number, theme: Theme) => void;
}

export const ThemeStep: React.FC<ThemeStepProps> = ({
  mode,
  themes,
  isLoading,
  status,
  selectedThemes,
  errorThemes,
  slotCount,
  onFetchThemes,
  onThemeImageError,
  onSelectThemeForSlot,
}) => {
  const FALLBACK_IMAGE = "https://images.unsplash.com/photo-1542038784456-1ea8e935640e?q=80&w=800&auto=format&fit=crop";

  useEffect(() => {
    if (status !== ASYNC_STATUS.INITIAL) {
      return;
    }

    void onFetchThemes();
  }, [onFetchThemes, status]);

  const renderThemeCard = (theme: Theme, slotIndex: number, isCompact: boolean = false) => {
    const isSelected = selectedThemes[slotIndex]?.id === theme.id;

    return (
      <button
        key={`${theme.id}-${slotIndex}`}
        onClick={() => onSelectThemeForSlot(slotIndex, theme)}
        className={cn(
          "group relative flex flex-col border text-left transition-all duration-300 hover:-translate-y-1.5",
          "border-white/12 bg-white/[0.06] shadow-[0_20px_60px_rgba(0,0,0,0.24)] backdrop-blur-md",
          isCompact ? "rounded-[1.75rem] p-4 md:p-5" : "rounded-[2.5rem] p-6 md:p-8",
          isSelected
            ? "border-primary ring-2 ring-primary/25 shadow-[0_24px_70px_rgba(0,0,0,0.3)]"
            : "hover:border-primary/45 hover:bg-white/[0.08] hover:shadow-[0_24px_70px_rgba(0,0,0,0.28)]"
        )}
      >
        <div
          className={cn(
            "relative overflow-hidden bg-muted",
            isCompact
              ? "-mx-4 -mt-4 mb-5 aspect-4/3 rounded-t-[1.75rem] md:-mx-5 md:-mt-5"
              : "-mx-6 -mt-6 mb-8 h-64 rounded-t-[2.5rem] md:-mx-8 md:-mt-8 md:h-72"
          )}
        >
          <Image
            src={errorThemes[theme.id] ? FALLBACK_IMAGE : getImageUrl(theme.imageUrl)}
            alt={theme.name}
            fill
            className="object-cover transition-transform duration-1000 group-hover:scale-110"
            sizes={isCompact ? "(max-width: 768px) 100vw, 33vw" : "(max-width: 768px) 100vw, 50vw"}
            onError={() => onThemeImageError(theme.id)}
          />
          <div className="absolute inset-0 bg-linear-to-t from-black/60 via-black/20 to-transparent transition-opacity duration-500 group-hover:opacity-80" />
        </div>

        <div className={cn("space-y-3", isCompact ? "px-1" : "")}>
          <h3
            className={cn(
              "font-serif text-foreground transition-colors duration-300 group-hover:text-primary",
              isCompact ? "text-lg md:text-xl" : "text-2xl"
            )}
          >
            {theme.name}
          </h3>
          <p
            className={cn(
              "text-muted-foreground/80 font-light leading-relaxed",
              isCompact ? "line-clamp-2 text-xs md:text-sm" : "line-clamp-3 text-sm"
            )}
          >
            {theme.description}
          </p>
        </div>
      </button>
    );
  };

  if (status === ASYNC_STATUS.INITIAL || isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-6 py-32">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />
        <p className="animate-pulse text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
          Setting the stage
        </p>
      </div>
    );
  }

  if (mode === "all-inclusive") {
    return (
      <div className="mx-auto max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="group relative overflow-hidden rounded-[2.5rem] border border-white/12 bg-white/[0.06] p-12 text-center shadow-[0_20px_60px_rgba(0,0,0,0.24)] backdrop-blur-md md:p-16"
        >
          <div className="mb-12 flex justify-center -space-x-8">
            {themes.map((theme, index) => (
              <motion.div
                key={theme.id}
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: 0.3 + (index * 0.1) }}
                className="relative z-1 h-24 w-24 overflow-hidden rounded-full border-[6px] border-card shadow-2xl md:h-32 md:w-32"
                whileHover={{ zIndex: 10, scale: 1.1, y: -10 }}
              >
                <Image
                  src={errorThemes[theme.id] ? FALLBACK_IMAGE : getImageUrl(theme.imageUrl)}
                  alt={theme.name}
                  fill
                  className="object-cover transition-transform duration-500"
                  sizes="(max-width: 768px) 100px, 150px"
                  onError={() => onThemeImageError(theme.id)}
                />
                <div className="absolute inset-0 bg-primary/5" />
              </motion.div>
            ))}
          </div>

          <h3 className="text-3xl font-serif text-foreground transition-colors duration-300 group-hover:text-primary md:text-4xl">
            All Themes Included
          </h3>

          <div className="absolute -bottom-24 -right-24 h-64 w-64 rounded-full bg-primary/5 blur-3xl" />
          <div className="absolute -left-24 -top-24 h-64 w-64 rounded-full bg-primary/5 blur-3xl" />
        </motion.div>
      </div>
    );
  }

  if (mode === "single" || slotCount === 1) {
    return (
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:gap-10">
          {themes.map((theme) => renderThemeCard(theme, 0))}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-16">
      {Array.from({ length: slotCount }).map((_, slotIndex) => (
        <div
          key={slotIndex}
          className="space-y-8"
        >
          <div className="border-b border-border/50 pb-4">
            <h3 className="text-2xl font-serif text-foreground">
              Slot {slotIndex + 1}
            </h3>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3 md:gap-8">
            {themes.map((theme) => renderThemeCard(theme, slotIndex, true))}
          </div>
        </div>
      ))}
    </div>
  );
};
